import styled from 'styled-components'

import { breakpoints } from '@/config/layout'
import { VisibleScrollbarStyle } from '@/styles/scrollbar'

import type { FC } from 'react'

type Props = {
  children: React.ReactNode
  image: string
  imagePosition?: React.CSSProperties['objectPosition']
}

/**
 * ContentImageLayout - Layout component that displays an image on the right side of the screen on Desktop and at the top on Mobile.
 */
export const ContentImageLayout: FC<Props> = ({ image, children, imagePosition = 'center' }) => {
  return (
    <StContentImageLayoutContainer>
      <StContentImageLayoutContent>{children}</StContentImageLayoutContent>
      <StContentImageLayoutImageContainer>
        <StContentImageLayoutImage src={image} imagePosition={imagePosition} />
      </StContentImageLayoutImageContainer>
    </StContentImageLayoutContainer>
  )
}

const StContentImageLayoutContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column-reverse;
  align-items: space-between;
  overflow: hidden;

  height: 100%;

  padding-bottom: var(--safe-area-inset-bottom);

  @media ${breakpoints.fromS} {
    flex-direction: row;
    height: 100%;
  }
`

const StContentImageLayoutContent = styled.main`
  flex-shrink: 0;
  width: 100%;
  padding: 48px 24px 0 24px;
  padding-bottom: calc(
    var(--safe-area-inset-bottom) + ${({ theme }) => theme.UI.SpacingPx.Space4}
  );

  overflow-y: auto;
  overflow-x: hidden;

  ${VisibleScrollbarStyle};

  display: flex;
  align-items: center;

  @media ${breakpoints.fromS} {
    width: 60%;
    max-width: 60%;
    min-width: 60%;
  }

  @media ${breakpoints.fromM} {
    align-items: center;
    width: 50%;
    max-width: 50%;
    min-width: 50%;
  }
`

const StContentImageLayoutImageContainer = styled.div`
  position: relative;
  min-height: 200px;
  width: 100%;
  flex: 1;
  flex-shrink: 1;

  @media ${breakpoints.fromS} {
    height: 100vh;
  }
`

const StContentImageLayoutImage = styled.img<{
  imagePosition: React.CSSProperties['objectPosition']
}>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${({ imagePosition }) => imagePosition ?? 'center'};
  border-radius: 0 0 80px 0;
  @media ${breakpoints.fromS} {
    border-radius: 0;
  }
`
